<script setup>
import { computed, useId } from 'vue'

const props = defineProps({
  modelValue: { type: String, default: '' },
  templates: { type: Array, required: true },
  disabled: { type: Boolean, default: false },
  label: { type: String, default: '리포트 종류' },
  loading: { type: Boolean, default: false },
  /** 표 안처럼 좁은 곳에서는 제목을 숨기고(스크린리더에는 읽힘) 버튼을 작게 쓴다. */
  compact: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue'])

const groupName = `report-template-${useId()}`
const enabledTemplates = computed(() => props.templates.filter(template => template.enabled))
const isDisabled = computed(() => props.disabled || props.loading || enabledTemplates.value.length === 0)

function select(templateId) {
  if (!isDisabled.value && templateId !== props.modelValue) emit('update:modelValue', templateId)
}
</script>

<template>
  <fieldset
    class="report-template-select"
    :class="{ 'is-compact': compact }"
    :aria-busy="loading"
    :disabled="isDisabled"
  >
    <legend :class="{ 'visually-hidden': compact }">{{ label }}</legend>
    <p v-if="loading || enabledTemplates.length === 0" class="report-template-empty">
      {{ loading ? '불러오는 중' : '사용 가능한 리포트 없음' }}
    </p>
    <div v-else class="report-template-options" role="radiogroup" :aria-label="label">
      <label
        v-for="template in enabledTemplates"
        :key="`${template.templateId}:${template.templateVersion}`"
        class="report-template-option"
        :class="{ 'is-selected': template.templateId === modelValue }"
      >
        <input
          type="radio"
          :name="groupName"
          :value="template.templateId"
          :checked="template.templateId === modelValue"
          :disabled="isDisabled"
          @change="select(template.templateId)"
        >
        <span class="report-template-swatch" :class="`is-${template.templateId}`" aria-hidden="true" />
        <span>{{ template.displayName }}</span>
      </label>
    </div>
  </fieldset>
</template>

<style scoped>
.report-template-select { display: grid; gap: 7px; min-width: 0; max-width: 100%; margin: 0; padding: 0; border: 0; color: var(--text-secondary); font-size: var(--text-sm); font-weight: 800; }
.report-template-select legend { padding: 0; margin-bottom: 7px; }
.report-template-options { display: flex; flex-wrap: wrap; gap: 6px; }
.report-template-option { position: relative; display: inline-flex; align-items: center; gap: 7px; min-height: 38px; padding: 0 12px; border: 1.5px solid var(--border-default); border-radius: var(--radius-sm); color: var(--text-primary); background: var(--bg-surface); font-weight: 700; white-space: nowrap; cursor: pointer; transition: border-color .15s, background-color .15s; }
.report-template-option input { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; opacity: 0; cursor: inherit; }
.report-template-option:hover { border-color: var(--action-primary); }
.report-template-option.is-selected { border-color: var(--action-primary); color: var(--action-primary); background: color-mix(in srgb, var(--action-primary) 8%, var(--bg-surface)); box-shadow: inset 0 0 0 1px var(--action-primary); }
.report-template-option:has(input:focus-visible) { outline: 3px solid color-mix(in srgb, var(--action-primary) 22%, transparent); outline-offset: 2px; }
.report-template-swatch { width: 12px; height: 12px; flex: none; border-radius: 50%; background: #247a78; box-shadow: inset 0 0 0 1px rgb(0 0 0 / 12%); }
/* 리포트 종류 표시: 기본형(전체)·강화형(보장분석 상세)·요약형(핵심) */
.report-template-swatch.is-basic { background: #247a78; }
.report-template-swatch.is-enhanced { background: #102A43; }
.report-template-swatch.is-summary { background: conic-gradient(#247a78 0 25%, #d6e1e8 25% 100%); }
.report-template-select:disabled .report-template-option { cursor: not-allowed; opacity: .55; }
.report-template-select:disabled .report-template-option:hover { border-color: var(--border-default); }
.report-template-empty { margin: 0; min-height: 38px; display: flex; align-items: center; color: var(--text-muted); font-weight: 700; }
.is-compact .report-template-option { min-height: 32px; padding: 0 9px; gap: 5px; font-size: 13px; }
.is-compact .report-template-options { flex-wrap: nowrap; }
.is-compact .report-template-empty { min-height: 32px; }
.visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
</style>
